import { TooltipContentType } from 'enums';
import _ from 'lodash';
import { Assessment } from '../../types/analysis-result.types';
import { ProfileValues } from '../../types/profile.types';

export interface ServerError {
  message: string;
  cause: string;
  code: string;
}

export interface HighlightType {
  categories: string[];
  highlightPosition: [number, number];
}

export interface ScoreProfiles {
  authenticity: number;
  regFocus: number;
  diversity?: number;
  jointPosAchieve: number;
  jointPosAffil: number;
  jointPosPower: number;
}

export interface Score {
  jointPosAchieve?: number;
  jointPosAffil?: number;
  jointPosPower?: number;
  authenticity?: number;
  regFocus?: number;
  diversity?: number;
}

export interface Percentiles {
  profile: ScoreProfiles;
  text: ScoreProfiles;
}

export interface Scores {
  diversity?: number;
  overall: number;
  percentiles: Percentiles;
}

export interface SidebarNote {
  text: string;
  level: number;
  key?: string;
  addwords?: string[];
  content?: string;
  title?: string;
  remove_categories: HighlightTextKeys[];
  remove_text?: string;
}

export interface SuggestionItem {
  contentType: TooltipContentType;
  content: GrammarReplacement | Paraphrase;
  index: number;
  highlightId: string;
}

export interface AWSuggestionContent {
  content: string;
  categories?: string[];
  score_improvement?: number;
  tooltipHighlights?: number[][];
  fingerprint?: string;
}

export interface GrammarReplacement {
  text: string;
  highlightPosition: [number, number];
  suggestions: AWSuggestionContent[];
  ranking_index: number;
}

export interface Paraphrase {
  highlightPosition: [number, number];
  replacedSpan?: { content: string; categories: string[] };
  text: string;
  suggestions: AWSuggestionContent[];
  ranking_index: number;
}

export interface ComplexSentence {
  pos: [number, number];
}

export interface Readability {
  highlightPosition: [number, number];
  categories: string[];
}

export interface HighlightTexts {
  jointPosAchieve?: string;
  jointPosAffil?: string;
  jointPosPower?: string;
  blue?: string;
  red?: string;
  green?: string;
  yellow?: string;
  feminine: string;
  masculine: string;
  complexSentences: string;
}

export type HighlightTextKeys = keyof HighlightTexts;

export interface ReadabilityTexts {
  complex: string;
  passive: string;
}

export interface HighlightColors {
  jointPosAchieve?: string;
  jointPosAffil?: string;
  jointPosPower?: string;
  blue?: string;
  red?: string;
  green?: string;
  yellow?: string;
  feminine: string;
  masculine: string;
  complexSentences: string;
  none?: string;
}

export class AugmentedAnalyses {
  public assessment: Assessment | undefined;
  public highlights: HighlightType[];
  public scores: Scores;
  public sidebarNotes: SidebarNote[];
  public suggestions: SuggestionTypes;
  public highlightTexts: HighlightTexts;
  public readabilityTexts: ReadabilityTexts;
  public highlightColors: HighlightColors;
  public complexSentences: ComplexSentence[];
  public unrecognized: boolean;
  public readability: Readability[];
  public error: ServerError;
  public coreCommit?: string;
  public profile: ProfileValues;
  public 'data-lang'?: string;
  public userDone?: boolean;

  constructor(response: any) {
    this.assessment = response.assessment;
    this.highlights = [];
    _.forEach(response.highlights, (item) => {
      this.highlights.push({
        highlightPosition: item.highlightPosition,
        categories: item.categories,
      });
    });
    this.scores = response.scores;
    this.sidebarNotes = response.sidebarNotes;
    this.highlightTexts = response.highlightTexts;
    this.readabilityTexts = response.readabilityTexts;
    this.complexSentences = response.complexSentences;
    this.readability = response.readability;
    this.highlightColors = response.highlightColors;
    this.unrecognized = response.unrecognized;
    this.suggestions = response.suggestions;
    this.error = response.error;
    this.coreCommit = response.coreCommit;
    this.profile = response.profile;
    this['data-lang'] = response['data-lang'];
    this.userDone = response['user_done'];
  }
}

export interface HighlightsResponse {
  highlights: HighlightType[];
  scores: Scores;
  complexSentences: ComplexSentence[];
  readability: Readability[];
  highlightTexts: HighlightTexts;
  readabilityTexts: ReadabilityTexts;
  highlightColors: HighlightTexts;
  unrecognized: boolean;
  error: ServerError;
  coreCommit?: string;
  sidebarNotes: SidebarNote[];
  profile: ProfileValues;
  'data-lang'?: string;
}

export interface WordSuggestionsResponse {
  assessment?: Assessment;
  suggestions: SuggestionTypes;
  highlights: HighlightType[];
  scores: Scores;
  complexSentences: ComplexSentence[];
  readability: Readability[];
  highlightTexts: HighlightTexts;
  readabilityTexts: ReadabilityTexts;
  highlightColors: HighlightTexts;
  unrecognized: boolean;
  coreCommit?: string;
  sidebarNotes: SidebarNote[];
  profile: ProfileValues;
  error: any;
  'data-lang'?: string;
  wordCount?: number;
  userDone?: boolean;
}

interface SuggestionTypes {
  grammarReplacement: GrammarReplacement[];
  paraphrases: Paraphrase[];
}

export interface SidebarNotesResponse {
  sidebarNotes: SidebarNote[];
  error: ServerError;
  'data-lang'?: string;
}
